'use client';

import { useRef, useState, useEffect } from 'react';
import debounce from 'lodash/fp/debounce';
import { cn } from '@fshr/shared-react';
import { Button, Link } from '@fshr/components';
import { compareLinkToPath } from './navigation';
import { type NavigationLink } from '@micro-apps/env';
import { usePathname } from 'next/navigation';

interface DesktopNavigationProps {
	navLinks: NavigationLink[];
	content: { moreLabel: string };
	fudgeFactor?: number;
}

export default function DesktopNavigation({
	navLinks = [],
	content,
	fudgeFactor = 155,
}: DesktopNavigationProps) {
	const ref = useRef<HTMLDivElement>(null);
	const [navLinksToDisplay, setNavLinksToDisplay] = useState<{
		navLinks: NavigationLink[];
		moreLinks: { left: NavigationLink[]; right: NavigationLink[] };
	}>({ navLinks, moreLinks: { left: [], right: [] } });
	const [moreLinksOpen, setMoreLinksOpen] = useState(false);
	const [moreLinksInitialized, setMoreLinksInitialized] = useState(false);
	const pathname = usePathname();

	const moreLinksLiRef = useRef<HTMLLIElement>(null);

	const handleClick = (evt: React.MouseEvent) => {
		evt.preventDefault();
		setMoreLinksOpen(!moreLinksOpen);
	};

	const mapMoreLinks = (links: NavigationLink[]) => {
		const left: NavigationLink[] = [];
		const right: NavigationLink[] = [];
		[...links].reverse().forEach((link, index) => {
			if (index % 2 === 0) {
				left.unshift(link);
			} else {
				right.unshift(link);
			}
		});
		return {
			left,
			right,
		};
	};
	useEffect(() => {
		const el = ref.current;
		const ul = el?.querySelector('ul');

		if (!el || !ul) {
			return;
		}

		const lis = Array.from(ul.children) as HTMLLIElement[];
		const moreLinksLi = lis.length > navLinks.length ? lis.pop() : undefined;
		const moreLinksButtonWidth = moreLinksLi?.clientWidth || 0;
		const gap = parseFloat(getComputedStyle(ul).columnGap) || 0;

		// Distance from the start edge of the list to the end of each link (plus its trailing gap).
		// Measured from widths rather than offsetLeft so LTR and RTL use the same maths.
		let runningWidth = 0;
		const linkEnds = lis.map(li => {
			runningWidth += li.clientWidth + gap;
			return runningWidth;
		});

		function handleResized(elWidth: number) {
			const availableWidth = elWidth - fudgeFactor - moreLinksButtonWidth;
			const splitIndex = linkEnds.findIndex(linkEnd => linkEnd > availableWidth);

			if (splitIndex === -1) {
				setNavLinksToDisplay({ navLinks, moreLinks: { left: [], right: [] } });
			} else {
				setNavLinksToDisplay({
					navLinks: navLinks.slice(0, splitIndex),
					moreLinks: mapMoreLinks(navLinks.slice(splitIndex)),
				});
			}

			el?.classList.remove('overflow-hidden');
			el?.classList.remove('opacity-0');
		}

		const debouncedHandleResized = debounce(200, handleResized);

		const resizeObserver = new ResizeObserver(entries => {
			el.classList.add('overflow-hidden');
			for (const entry of entries) {
				debouncedHandleResized(entry.contentRect.width);
			}
		});

		resizeObserver.observe(el);
		setMoreLinksInitialized(true);
		return () => {
			resizeObserver.unobserve(el);
		};
	}, []);

	const totalMoreLinks =
		navLinksToDisplay.moreLinks.left.length + navLinksToDisplay.moreLinks.right.length;
	return (
		<nav ref={ref} className="overflow-hidden opacity-0 transition-opacity">
			<ul className="flex flex-row flex-nowrap gap-[35px] relative py-[6px] leading-[18px] items-center h-[30px]">
				{navLinksToDisplay.navLinks.map(({ text, link }) => (
					<li className="h-[18px]" key={text}>
						<Link
							href={link}
							intent="unstyled"
							prefetch={false}
							className={cn([
								'whitespace-nowrap',
								'text-label',
								'!font-bold',
								'!tracking-[2.1px]',
								'!leading-[1.333333em]',
								'hover:text-neutral-900',
								'focus:text-neutral-900',
								'transition-colors',
							])}
						>
							<span
								className={cn({
									'!leading-[18px] border-b border-b-[currentColor]': compareLinkToPath(
										link,
										pathname
									),
								})}
							>
								{text}
							</span>
						</Link>
					</li>
				))}
				{(!moreLinksInitialized || totalMoreLinks > 0) && (
					<li ref={moreLinksLiRef} className="group relative">
						<Button
							intent="unstyled"
							className={cn(
								[
									'peer',
									'text-label',
									'!font-bold',
									'!tracking-[2.1px]',
									'!leading-[1.333333em]',
									'hover:text-neutral-900',
									'focus:text-neutral-900',
									'transition-colors',
									'opacity-0',
								],
								{ 'opacity-100': totalMoreLinks > 0 }
							)}
							onClick={handleClick}
						>
							<span
								className={cn({
									'border-b border-b-[currentColor]': [
										...navLinksToDisplay.moreLinks.left,
										...navLinksToDisplay.moreLinks.right,
									].some(link => compareLinkToPath(link.link, pathname)),
								})}
							>
								{content.moreLabel}
							</span>
						</Button>
						{totalMoreLinks > 0 && (
							<div
								className={cn(
									[
										'peer-hover:block',
										'group-focus:block',
										'group-hover:block',
										'focus-within:block',
										'hover:block',
										'focus:block',
										'absolute',
										'top-[15px]',
										'pt-[30px]',
										'left-[52%]',
										'-translate-x-[52%]',
										'w-[425px]',
										'z-49',
									],
									{ hidden: !moreLinksOpen }
								)}
								onClick={handleClick}
							>
								<div
									className={cn(
										[
											'relative',
											'bg-white',
											'text-black',
											'w-full',
											"before:content-['']",
											'before:border-[14px]',
											'before:border-transparent',
											'before:border-b-white',
											'before:absolute',
											'before:-top-[28px]',
											'before:left-[49%]',
											'before:-ml-100',
											'shadow-[0px_2px_4px_0px_rgba(0,_0,_0,_0.5)]',
											'pt-[42px]',
											'pb-[28px]',
											'px-300',
											'grid',
											'gap-y-[17px]',
											'border-t',
											'border-t-[#d8d8d8]',
										],
										{
											'grid-cols-2':
												navLinksToDisplay.moreLinks.left.length +
													navLinksToDisplay.moreLinks.right.length >
												3,
											'grid-cols-1':
												navLinksToDisplay.moreLinks.left.length +
													navLinksToDisplay.moreLinks.right.length <=
												3,
										}
									)}
								>
									{navLinksToDisplay.moreLinks.left.length > 0 && (
										<ul className="px-300 flex flex-col gap-[17px]">
											{navLinksToDisplay.moreLinks.left.map(({ text, link }) => (
												<li key={text}>
													<Link
														href={link}
														intent="unstyled"
														prefetch={false}
														className={cn([
															'block',
															'text-label',
															'!font-bold',
															'!tracking-[2.1px]',
															'!leading-[1.333333em]',
															'hover:text-neutral-900',
															'focus:text-neutral-900',
															'transition-colors',
														])}
													>
														<span
															className={cn({
																'!leading-[18px] border-b border-b-[currentColor]':
																	compareLinkToPath(link, pathname),
															})}
														>
															{text}
														</span>
													</Link>
												</li>
											))}
										</ul>
									)}
									{navLinksToDisplay.moreLinks.right.length > 0 && (
										<ul className="px-300 flex flex-col gap-[17px]">
											{navLinksToDisplay.moreLinks.right.map(({ text, link }) => (
												<li key={text}>
													<Link
														href={link}
														intent="unstyled"
														prefetch={false}
														className={cn([
															'block',
															'text-label',
															'!font-bold',
															'!tracking-[2.1px]',
															'!leading-[1.333333em]',
															'hover:text-neutral-900',
															'focus:text-neutral-900',
															'transition-colors',
														])}
													>
														<span
															className={cn({
																'!leading-[18px] border-b border-b-[currentColor]':
																	compareLinkToPath(link, pathname),
															})}
														>
															{text}
														</span>
													</Link>
												</li>
											))}
										</ul>
									)}
								</div>
							</div>
						)}
					</li>
				)}
			</ul>
		</nav>
	);
}
